import React, { useCallback, useMemo } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { PickupStoreSelector } from 'features/store/pages/pickup-store-selector';

import { isStoreAllowed } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/is-store-allowed';
import { navigateToUnavailableOffersFeed } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/navigate-to-unavailable-offers-feed';
import { UNAVAILABLE_OFFER_REASON } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/unavailable-offer-reason';
import { LoyaltyServiceOfferStoreRestrictionMode } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';

function parseParam(value?: string | string[]): string | undefined {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
}

function parseStoreRestrictionMode(
  value?: string | string[]
): LoyaltyServiceOfferStoreRestrictionMode | undefined {
  const mode = parseParam(value);
  return Object.values(LoyaltyServiceOfferStoreRestrictionMode).includes(
    mode as LoyaltyServiceOfferStoreRestrictionMode
  )
    ? (mode as LoyaltyServiceOfferStoreRestrictionMode)
    : undefined;
}

function parseAvailableStoreIds(value?: string | string[]): string[] {
  return (parseParam(value) ?? '')
    .split(',')
    .map(storeId => storeId.trim())
    .filter(Boolean);
}

/**
 * Minimal store locator for menu and offer unavailable Tap-here flows.
 * Offer flows filter the results using loyalty-service store restrictions.
 * Menu flows retain their existing current-store unavailable treatment.
 */
export default function MinimalStoreLocator() {
  const router = useRouter();
  const {
    offerEngineId,
    unavailableItemId,
    storeRestrictionMode,
    availableStoreIds,
  } = useLocalSearchParams<{
    offerEngineId?: string | string[];
    unavailableItemId?: string | string[];
    storeRestrictionMode?: string | string[];
    availableStoreIds?: string | string[];
  }>();
  const parsedOfferEngineId = parseParam(offerEngineId);
  const isOfferFlow = Boolean(parsedOfferEngineId);
  // Only the feed's unavailable card sends `unavailableItemId`, so its presence
  // means the guest opened this selector on purpose from that card.
  const openedFromUnavailableCard = Boolean(parseParam(unavailableItemId));
  const restrictionMode = parseStoreRestrictionMode(storeRestrictionMode);
  const eligibleStoreIds = useMemo(() => parseAvailableStoreIds(availableStoreIds), [
    availableStoreIds,
  ]);
  const isStoreEligible = useCallback(
    (store: { number?: string | null }) =>
      isStoreAllowed(store.number, restrictionMode, eligibleStoreIds),
    [eligibleStoreIds, restrictionMode]
  );

  // Every nearby restaurant violates the offer's restriction, so there is
  // nothing to choose here. Return to the feed with the offer still pinned and
  // let the card explain that no nearby restaurant can redeem it.
  //
  // Not when the guest came from that card: returning them to the card they
  // just tapped is a no-op they cannot escape. Leaving them here keeps the
  // address search available so they can look somewhere else.
  const handleNoEligibleStores = useCallback(() => {
    if (!parsedOfferEngineId) {
      return;
    }
    navigateToUnavailableOffersFeed(
      router,
      parsedOfferEngineId,
      UNAVAILABLE_OFFER_REASON.NO_NEARBY_STORES
    );
  }, [parsedOfferEngineId, router]);

  return (
    <PickupStoreSelector
      nearbyOnly
      isOffersSelection
      headerText={t('selectLocation')}
      pinCurrentAsUnavailable={openedFromUnavailableCard}
      isStoreEligible={isOfferFlow ? isStoreEligible : undefined}
      storeFilter={isOfferFlow ? isStoreEligible : undefined}
      onNoEligibleStores={
        isOfferFlow && !openedFromUnavailableCard ? handleNoEligibleStores : undefined
      }
      ineligibleStoreMessage={t('notAvailableInThisRestaurant')}
    />
  );
}
